import { db, auth } from '@/js/firebase';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
  // Timestamp,
  onSnapshot,
  orderBy,
  limit
} from 'firebase/firestore';
import { logger } from './loggerService';
import { schedulingRulesService } from './schedulingRulesService';
import { withDefaultAssignees } from '@/utils/eventAssignees';
import { toastBridge } from './toastBridge';
import { deleteCalendarEventSubcollections } from './firestoreUtils';
import { MS_PER_HOUR } from './dateService';
import { valuesEqual } from '@/utils/valueCompare';
import { mintBunnyAssetUrl } from './bunnyUploadService';
import { BUNNY_URL_INTENT } from '@/utils/bunnyConstants';

class CalendarEventService {
  constructor() {
    this.logger = logger;
    this.toast = toastBridge;
  }

  /**
   * @param {Object} eventData
   * @param {boolean} [showToast]
   * @param {Object} [options]
   * @param {boolean} [options.skipDefaultAssignees] - Don't prefill the crew
   *   from settings/schedulingRules (used where the caller has already decided).
   */
  async createEvent(eventData, showToast = true, { skipDefaultAssignees = false } = {}) {
    try {
      const eventsRef = collection(db, 'calendar');

      // Prefill the crew for this production type. Only fills an empty
      // assignedUserIds, and only on create — see utils/eventAssignees.js.
      const withCrew = skipDefaultAssignees
        ? eventData
        : withDefaultAssignees(eventData, await schedulingRulesService.getCachedSettings());

      const newEvent = {
        ...withCrew,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(eventsRef, newEvent);

      // Log event creation
      await this.logActivity(
        docRef.id,
        'created',
        null,
        null,
        null,
        `Event created: ${eventData.title || 'Untitled Event'}`
      );

      this.logger.info('CalendarEventService', `Calendar event created with ID: ${docRef.id}`);
      if (showToast) {
        this.toast.success('Calendar event created successfully');
      }

      return { success: true, id: docRef.id };
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to create calendar event: ${error.message}`);
      this.toast.error('Failed to create calendar event');
      throw error;
    }
  }

  async updateEvent(eventId, updates, showToast = true) {
    try {
      const eventRef = doc(db, 'calendar', eventId);

      // Get the current event data before updating
      const currentEvent = await this.getEvent(eventId);

      const cleanedUpdates = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      };

      await updateDoc(eventRef, cleanedUpdates);

      // Log activity for ALL field changes (dynamic tracking)
      // Skip internal/metadata fields
      const skipFields = ['updatedAt', 'updatedBy', 'createdAt', 'createdBy', 'migrated', 'migratedAt', 'googleCalendarEventIds'];

      for (const field of Object.keys(updates)) {
        // Skip internal fields
        if (skipFields.includes(field)) continue;

        let oldValue = currentEvent[field];
        let newValue = updates[field];

        // Skip if new value is undefined
        if (newValue === undefined) continue;

        // Only log if the value actually changed. valuesEqual covers empty-vs-empty
        // and Timestamp-vs-Date, which a bare !== reports as phantom changes.
        if (!valuesEqual(oldValue, newValue)) {
          // Format field name for display
          const fieldLabel = this.formatEventFieldName(field);

          await this.logActivity(
            eventId,
            'field_update',
            fieldLabel,
            oldValue,
            newValue,
            null
          );
        }
      }

      this.logger.info('CalendarEventService', `Calendar event ${eventId} updated`);

      if (showToast) {
        this.toast.success('Calendar event updated successfully');
      }

      return { success: true };
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to update calendar event: ${error.message}`);
      this.toast.error('Failed to update calendar event');
      throw error;
    }
  }

  async deleteEvent(eventId) {
    try {
      // Get the event data before deleting
      const event = await this.getEvent(eventId);

      // Delete subcollections first (productionSchedules, activity)
      const subcollectionResults = await deleteCalendarEventSubcollections(eventId);
      this.logger.info('CalendarEventService', `Deleted subcollections for event ${eventId}`, subcollectionResults);

      // Delete the event document
      const eventRef = doc(db, 'calendar', eventId);
      await deleteDoc(eventRef);

      this.logger.info('CalendarEventService', `Calendar event ${eventId} deleted (${event.title || 'Untitled Event'})`);
      this.toast.success('Calendar event deleted successfully');

      return { success: true };
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to delete calendar event: ${error.message}`);
      this.toast.error('Failed to delete calendar event');
      throw error;
    }
  }

  async getEvent(eventId) {
    // Guard against missing/stringified-undefined ids reaching Firestore's doc(),
    // which throws a cryptic "Cannot read properties of undefined (reading 'indexOf')".
    if (!eventId || typeof eventId !== 'string' || eventId === 'undefined') {
      const error = new Error(`Calendar event ${eventId} not found`);
      this.logger.warn('CalendarEventService', `getEvent called with invalid id: ${eventId}`);
      throw error;
    }
    try {
      const eventRef = doc(db, 'calendar', eventId);
      const eventSnap = await getDoc(eventRef);
      
      if (eventSnap.exists()) {
        return { id: eventSnap.id, ...eventSnap.data() };
      }
      
      throw new Error(`Calendar event ${eventId} not found`);
    } catch (error) {
      const level = error.message.includes('not found') ? 'warn' : 'error';
      this.logger[level]('CalendarEventService', `Failed to get calendar event: ${error.message}`);
      throw error;
    }
  }

  async getEventsByCompany(companyId) {
    try {
      const eventsRef = collection(db, 'calendar');
      const q = query(eventsRef, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);
      
      const events = [];
      querySnapshot.forEach((doc) => {
        events.push({ id: doc.id, ...doc.data() });
      });
      
      return events;
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to get events for company: ${error.message}`);
      throw error;
    }
  }

  async getEventsByProject(projectId) {
    try {
      const eventsRef = collection(db, 'calendar');
      const q = query(eventsRef, where('projectId', '==', projectId));
      const querySnapshot = await getDocs(q);
      
      const events = [];
      querySnapshot.forEach((doc) => {
        events.push({ id: doc.id, ...doc.data() });
      });
      
      return events;
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to get events for project: ${error.message}`);
      throw error;
    }
  }

  async getEventsByDateRange(companyId, startDate, endDate) {
    try {
      const eventsRef = collection(db, 'calendar');
      const q = query(
        eventsRef,
        where('companyId', '==', companyId),
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );
      const querySnapshot = await getDocs(q);
      
      const events = [];
      querySnapshot.forEach((doc) => {
        events.push({ id: doc.id, ...doc.data() });
      });
      
      return events;
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to get events by date range: ${error.message}`);
      throw error;
    }
  }

  formatEventForFullCalendar(event) {
    const baseEvent = {
      id: event.id,
      title: event.title || event.eventTitle,
      start: this.formatEventDateTime(event.date, event.startTime),
      allDay: event.allDay || false,
      extendedProps: {
        eventType: event.eventType,
        projectId: event.projectId,
        companyId: event.companyId,
        description: event.description,
        location: event.location,
        uvpDeduction: event.uvpDeduction
      }
    };

    if (!event.allDay && event.endTime) {
      baseEvent.end = this.formatEventDateTime(event.date, event.endTime);
    } else if (event.dateEnd) {
      baseEvent.end = event.dateEnd;
    }

    if (event.eventType) {
      baseEvent.classNames = [this.getEventTypeClassName(event.eventType)];
      baseEvent.backgroundColor = this.getEventTypeColor(event.eventType);
    }

    return baseEvent;
  }

  formatEventDateTime(date, time) {
    if (!time) return date;
    
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    return `${dateStr}T${time}`;
  }

  getEventTypeColor(eventType) {
    const colors = {
      'Production': '#00d1b2',
      'Post-Production': '#3e8ed0',
      'Meeting': '#ffe08a',
      'Deadline': '#f14668',
      'Review': '#48c78e',
      'Other': '#b5b5b5'
    };
    return colors[eventType] || colors['Other'];
  }

  getEventTypeClassName(eventType) {
    const classMap = {
      'Production': 'event-production',
      'Post-Production': 'event-post-production',
      'Meeting': 'event-meeting',
      'Deadline': 'event-deadline',
      'Review': 'event-review',
      'Other': 'event-other'
    };
    return classMap[eventType] || classMap['Other'];
  }

  /**
   * Format a time-off request as a FullCalendar event object.
   * Used to merge time-off into calendar views.
   */
  formatTimeOffForFullCalendar(request) {
    const typeLabels = { vacation: 'Vacation', sick: 'Sick Day' }
    const typeLabel = typeLabels[request.type] || request.type

    // Convert Firestore Timestamps to dates
    const startDate = request.startDate?.toDate?.()
      || new Date(request.startDate)
    const endDate = request.endDate?.toDate?.()
      || new Date(request.endDate)

    const isHalfDay = request.isHalfDay || false

    const event = {
      id: `timeoff-${request.id}`,
      title: `${request.userName} - ${typeLabel}${isHalfDay ? ' (½)' : ''}${request.status === 'pending' ? ' (Pending)' : ''}`,
      classNames: ['is-time-off'],
      backgroundColor: 'hsl(190, 70%, 45%)',
      extendedProps: {
        isTimeOff: true,
        requestId: request.id,
        userId: request.userId,
        timeOffType: request.type,
        timeOffStatus: request.status
      }
    }

    if (isHalfDay) {
      // Half-day: startDate/endDate are real UTC instants of the half-day window
      event.start = startDate
      event.end = endDate
      event.allDay = false
    } else {
      // Full-day: all-day banner spanning the date range
      event.start = startDate.toISOString().split('T')[0]
      // FullCalendar allDay end is exclusive, so add 1 day
      const endPlusOne = new Date(endDate)
      endPlusOne.setDate(endPlusOne.getDate() + 1)
      event.end = endPlusOne.toISOString().split('T')[0]
      event.allDay = true
    }

    return event
  }

  async migrateEventDates(eventId, updates) {
    try {
      const eventRef = doc(db, 'calendar', eventId);
      
      await updateDoc(eventRef, {
        ...updates,
        migrated: true,
        migratedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      this.logger.info('CalendarEventService', `Event ${eventId} dates migrated`);
      
      return { success: true };
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to migrate event dates: ${error.message}`);
      throw error;
    }
  }

  // Production Plan Subcollection Methods
  async addProductionSchedule(eventId, scheduleData) {
    try {
      const schedulesRef = collection(db, 'calendar', eventId, 'productionSchedules');
      
      const newSchedule = {
        ...scheduleData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(schedulesRef, newSchedule);
      
      // Update event to indicate it has a production schedule
      await this.updateEvent(eventId, { hasProductionSchedule: true }, false);
      
      this.logger.info('CalendarEventService', `Production schedule added to event ${eventId}`);
      
      return { success: true, id: docRef.id };
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to add production schedule: ${error.message}`);
      throw error;
    }
  }

  /**
   * Load an event's production schedules.
   *
   * The stored `url` is dead weight: pre-Bunny docs point at Firebase Storage
   * objects the migration deleted, and Bunny docs store a signed URL that
   * expires in ~1h. Always re-mint from `assetId`; a schedule that can't be
   * minted comes back with `url: null` so callers can hide the link instead
   * of handing the user a 403.
   *
   * @param {boolean} mintUrls - false to skip minting (count-only callers).
   */
  async getProductionSchedules(eventId, mintUrls = true) {
    try {
      const schedulesRef = collection(db, 'calendar', eventId, 'productionSchedules');
      const querySnapshot = await getDocs(schedulesRef);

      const schedules = [];
      querySnapshot.forEach((doc) => {
        schedules.push({ id: doc.id, ...doc.data() });
      });

      if (!mintUrls) return schedules;

      return await Promise.all(
        schedules.map(async (s) => ({
          ...s,
          url: s.assetId
            ? await mintBunnyAssetUrl(s.assetId, BUNNY_URL_INTENT.DOWNLOAD)
            : null,
        }))
      );
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to get production schedules: ${error.message}`);
      throw error;
    }
  }

  async deleteProductionSchedule(eventId, scheduleId) {
    try {
      const scheduleRef = doc(db, 'calendar', eventId, 'productionSchedules', scheduleId);
      await deleteDoc(scheduleRef);
      
      // Check if there are any remaining schedules
      const remainingSchedules = await this.getProductionSchedules(eventId, false);
      if (remainingSchedules.length === 0) {
        await this.updateEvent(eventId, { hasProductionSchedule: false }, false);
      }
      
      this.logger.info('CalendarEventService', `Production schedule ${scheduleId} deleted from event ${eventId}`);
      
      return { success: true };
    } catch (error) {
      this.logger.error('CalendarEventService', `Failed to delete production schedule: ${error.message}`);
      throw error;
    }
  }

  calculateUVPDeduction(event, company) {
    if (!company?.uvp || !event.uvpDeduction) {
      return 0;
    }

    const startTime = new Date(`2000-01-01T${event.startTime}`);
    const endTime = new Date(`2000-01-01T${event.endTime}`);
    let duration = (endTime - startTime) / MS_PER_HOUR;

    if (event.breakTime && event.breakTime > 0) {
      const breakHours = event.breakTime / 60;
      duration -= breakHours;

      if (event.eventType === 'Remote Production') {
        duration -= breakHours * 0.5;
      }
    }

    if (company.days) {
      const daysDeducted = duration / 8;
      return Math.round(daysDeducted * 100) / 100;
    } else {
      return Math.round(duration * 100) / 100;
    }
  }

  /**
   * Log an activity entry for a calendar event
   * @param {string} eventId - Calendar event ID
   * @param {string} action - Action type (created, field_update, deleted, etc.)
   * @param {string} field - Field name or description
   * @param {any} oldValue - Previous value
   * @param {any} newValue - New value
   * @param {string} note - Optional note
   */
  async logActivity(eventId, action, field = null, oldValue = null, newValue = null, note = null) {
    try {
      const user = auth.currentUser;
      if (!user) {
        this.logger.warn('[CalendarEventService] Cannot log activity - no authenticated user');
        return;
      }

      // Determine user type from custom claims
      let userType = 'system';
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims;

        if (claims.admin || claims.super) {
          userType = 'admin';
        } else if (claims.user) {
          userType = 'client';
        }
      }

      const userName = user.displayName || user.email || 'Unknown User';

      // Create activity log entry
      const activityData = {
        action,
        field,
        oldValue: oldValue !== undefined ? oldValue : null,
        newValue: newValue !== undefined ? newValue : null,
        userId: user.uid,
        userName,
        userType,
        timestamp: serverTimestamp(),
        note
      };

      // Remove null values to keep documents clean
      Object.keys(activityData).forEach(key => {
        if (activityData[key] === null || activityData[key] === undefined) {
          delete activityData[key];
        }
      });

      const activityRef = collection(db, 'calendar', eventId, 'activity');
      await addDoc(activityRef, activityData);

      this.logger.debug('[CalendarEventService] Activity logged', {
        eventId,
        action,
        field
      });
    } catch (error) {
      this.logger.error('[CalendarEventService] Failed to log activity', {
        error: error.message,
        eventId,
        action
      });
      // Don't throw - activity logging shouldn't break the main flow
    }
  }

  /**
   * Get activity logs for a calendar event
   * @param {string} eventId - Calendar event ID
   * @param {number} limitCount - Maximum number of logs to fetch (default 50)
   * @returns {Promise<Array>} Array of activity log entries
   */
  async getEventActivity(eventId, limitCount = 50) {
    try {
      const activityRef = collection(db, 'calendar', eventId, 'activity');
      const q = query(activityRef, orderBy('timestamp', 'desc'), limit(limitCount));

      const querySnapshot = await getDocs(q);
      const activities = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          ...data,
          // Convert Firestore Timestamp to Date for easier handling
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp
        });
      });

      this.logger.debug('[CalendarEventService] Fetched event activity', {
        eventId,
        count: activities.length
      });

      return activities;
    } catch (error) {
      this.logger.error('[CalendarEventService] Failed to get event activity', {
        error: error.message,
        eventId
      });
      throw error;
    }
  }

  /**
   * Subscribe to real-time calendar event activity updates
   * @param {string} eventId - Calendar event ID
   * @param {Function} callback - Callback function to receive activity updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToEventActivity(eventId, callback) {
    try {
      const activityRef = collection(db, 'calendar', eventId, 'activity');
      const q = query(activityRef, orderBy('timestamp', 'desc'), limit(50));

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const activities = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          activities.push({
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp
          });
        });

        callback(activities);
      }, (error) => {
        this.logger.error('[CalendarEventService] Error in activity subscription', {
          error: error.message,
          eventId
        });
      });

      this.logger.debug('[CalendarEventService] Subscribed to event activity', {
        eventId
      });

      return unsubscribe;
    } catch (error) {
      this.logger.error('[CalendarEventService] Failed to subscribe to event activity', {
        error: error.message,
        eventId
      });
      throw error;
    }
  }

  /**
   * Format calendar event field names for display in activity logs
   * @param {string} field - Field name
   * @returns {string} Formatted field label
   */
  formatEventFieldName(field) {
    const fieldMap = {
      title: 'Event Title',
      type: 'Event Type',
      date: 'Event Date',
      dateEnd: 'End Date',
      startTime: 'Start Time',
      endTime: 'End Time',
      allDay: 'All Day',
      location: 'Location',
      description: 'Description',
      projectId: 'Project',
      companyId: 'Company',
      uvpDeduction: 'UVP Deduction',
      breakTime: 'Break Time',
      hasProductionSchedule: 'Production Plan',
      eventTitle: 'Event Title',
      postEventReconciliation: 'Post-Event Reconciliation'
    };

    return fieldMap[field] || field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
  }
}

export const calendarEventService = new CalendarEventService();